import { Injectable } from '@angular/core';
import { TabControlConfig, TabInfo } from '../interfaces/builder-screen.models';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

@Injectable()
export class TabControlService {

  private builderService: any = null;

  // Método para injetar o service depois da criação (evita dependência circular)
  setBuilderService(builderService: any): void {
    this.builderService = builderService;
  }

  /**
   * Aplica controle de visibilidade às abas baseado no estado do switch
   */
  applyTabControl(switchValue: boolean, tabControl: TabControlConfig): void {
    if (!tabControl.enabled) {
      return;
    }

    const action = switchValue ? tabControl.onTrueAction : tabControl.onFalseAction;
    const targetTabs = tabControl.targetTabs;
    const mode = tabControl.mode;
    
    const allTabs = this.getAvailableTabs();
    
    allTabs.forEach(tab => {
      const isTarget = targetTabs.includes(tab.id);
      let shouldShow: boolean;
      
      if (mode === 'include') {
        shouldShow = action === 'show' ? isTarget : !isTarget;
      } else { // exclude
        shouldShow = action === 'show' ? !isTarget : isTarget;
      }
      
      this.setTabVisibility(tab.id, shouldShow);
    });
  }

  /**
   * Obtém lista de abas disponíveis no builder atual
   */
  getAvailableTabs(): TabInfo[] {
    if (!this.builderService) {
      return [];
    }
    
    const currentNodes = this.builderService.currentState.nodes;
    const tabsNode = currentNodes.find((n: any) => n.type === 'tabs');
    
    if (!tabsNode?.props || !(tabsNode.props as any).items) {
      return [];
    }
    
    return (tabsNode.props as any).items.map((item: any) => ({
      id: item.id,
      name: item.name,
      disabled: item.disabled || false
    }));
  }

  /**
   * Valida se as referências de abas são válidas
   */
  validateTabReferences(targetTabs: string[]): ValidationResult {
    const availableTabs = this.getAvailableTabs();
    const availableTabIds = availableTabs.map(tab => tab.id);
    const errors: string[] = [];

    if (targetTabs.length === 0) {
      errors.push('builderScreen.switch.tabControl.validation.noTabs');
    }

    targetTabs.forEach(tabId => {
      if (!availableTabIds.includes(tabId)) {
        errors.push(`builderScreen.switch.tabControl.validation.invalidTab`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Aplica controle inicial de abas baseado no valor padrão do switch
   */
  applyInitialTabControl(defaultValue: boolean, tabControl: TabControlConfig): void {
    if (tabControl.enabled) {
      this.applyTabControl(defaultValue, tabControl);
    }
  }

  /**
   * Define a visibilidade de uma aba específica
   * Implementa a lógica usando classes CSS para mostrar/ocultar abas
   */
  private setTabVisibility(tabId: string, visible: boolean): void {
    // Buscar o elemento da aba no DOM
    const tabElement = document.querySelector(`[data-tab-id="${tabId}"]`);
    
    if (tabElement) {
      if (visible) {
        tabElement.classList.remove('tab-hidden');
        tabElement.classList.add('tab-visible');
      } else {
        tabElement.classList.remove('tab-visible');
        tabElement.classList.add('tab-hidden');
      }
    }

    // Também aplicar ao conteúdo da aba
    const tabContentElement = document.querySelector(`[data-tab-content-id="${tabId}"]`);
    if (tabContentElement) {
      if (visible) {
        tabContentElement.classList.remove('tab-content-hidden');
        tabContentElement.classList.add('tab-content-visible');
      } else {
        tabContentElement.classList.remove('tab-content-visible');
        tabContentElement.classList.add('tab-content-hidden');
      }
    }
  }

  /**
   * Remove todos os controles de visibilidade aplicados (útil para reset)
   */
  resetAllTabVisibility(): void {
    const allTabs = this.getAvailableTabs();
    
    allTabs.forEach(tab => {
      const tabElement = document.querySelector(`[data-tab-id="${tab.id}"]`);
      const tabContentElement = document.querySelector(`[data-tab-content-id="${tab.id}"]`);
      
      if (tabElement) {
        tabElement.classList.remove('tab-hidden', 'tab-visible');
      }
      
      if (tabContentElement) {
        tabContentElement.classList.remove('tab-content-hidden', 'tab-content-visible');
      }
    });
  }

  /**
   * Métodos para Event Scripting System
   */
  
  /**
   * Mostra uma aba específica
   */
  showTab(tabId: string): void {
    this.setTabVisibility(tabId, true);
  }

  /**
   * Esconde uma aba específica
   */
  hideTab(tabId: string): void {
    this.setTabVisibility(tabId, false);
  }

  /**
   * Habilita uma aba específica (remove disabled)
   */
  enableTab(tabId: string): void {
    this.setTabDisabled(tabId, false);
  }

  /**
   * Desabilita uma aba específica
   */
  disableTab(tabId: string): void {
    this.setTabDisabled(tabId, true);
  }

  /**
   * Define o estado disabled de uma aba
   */
  private setTabDisabled(tabId: string, disabled: boolean): void {
    if (!this.builderService) {
      return;
    }

    const currentNodes = this.builderService.currentState.nodes;
    const tabsNode = currentNodes.find((n: any) => n.type === 'tabs');
    
    if (!tabsNode?.props || !(tabsNode.props as any).items) {
      return;
    }

    const items = (tabsNode.props as any).items;
    const tabIndex = items.findIndex((item: any) => item.id === tabId);
    
    if (tabIndex !== -1) {
      items[tabIndex].disabled = disabled;
      
      // Atualizar o node no builder service
      this.builderService.updateNodeProps(tabsNode.id, {
        items: [...items]
      });
    }

    // Também aplicar visualmente no DOM
    const tabElement = document.querySelector(`[data-tab-id="${tabId}"]`);
    if (tabElement) {
      if (disabled) {
        tabElement.classList.add('ant-tabs-tab-disabled');
        tabElement.setAttribute('aria-disabled', 'true');
      } else {
        tabElement.classList.remove('ant-tabs-tab-disabled');
        tabElement.setAttribute('aria-disabled', 'false');
      }
    }
  }
}
